'use client';

import Link from 'next/link';
import { motion, useReducedMotion } from 'framer-motion';
import BgOrbs from './BgOrbs';
import {
  SUBSCRIPTION_PLANS,
  yearlyPerMonthRub,
  yearlyFreeMonths,
} from '@shared/services/subscriptionPlans';

/**
 * PricingSection — тарифы перед FAQ. Два плана без переключателя:
 * «Бесплатно» (приглушённая карточка) и «Подписка» (акцентная, обе цены сразу —
 * месяц крупно, год строкой с выгодой). CTA подписки ведёт на /unlimited,
 * бесплатный CTA скроллит к финальной сцене с выбором TG/VK.
 * Тёмная секция в цепочке Scene10 → Pricing → FAQ → Final.
 */

const FREE_FEATURES = [
  'Сохранение ссылок, заметок и файлов — без ограничений',
  'Боты в Telegram и VK + веб-каталог',
  'Категории, поиск, разбор, напоминания',
  'Распознавание текста и подборки — по 5 в день',
];

const PAID_FEATURES = [
  'Всё из бесплатного тарифа',
  'Распознавание текста со скриншотов — без дневного лимита',
  'Подборки «Чем заняться?» — без дневного лимита',
  'Новые возможности подписки — сразу, как появятся',
];

function CheckIcon({ accent = false }: { accent?: boolean }) {
  return (
    <svg
      className={`w-4 h-4 shrink-0 mt-1 ${accent ? 'text-[#f5a85a]' : 'text-[#8faa8d]'}`}
      fill="none"
      stroke="currentColor"
      strokeWidth={2.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path d="M5 13l4 4L19 7" />
    </svg>
  );
}

export default function PricingSection() {
  const reduced = useReducedMotion() ?? false;
  const monthly = SUBSCRIPTION_PLANS.monthly;
  const yearly = SUBSCRIPTION_PLANS.yearly;

  const scrollToFinal = () => {
    document.getElementById('scene-final')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="scene-pricing" className="relative w-full bg-[#0d1a0c] py-20 sm:py-24 px-5 sm:px-8 overflow-hidden">
      <BgOrbs variant="warm" />

      <div className="relative max-w-4xl mx-auto">
        {/* Заголовок */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="text-center mb-10 sm:mb-12"
        >
          <h2
            className="font-display font-bold text-white leading-[1] tracking-[-0.02em] mb-3"
            style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)' }}
          >
            Сколько это <span className="text-[#f5a85a]">стоит?</span>
          </h2>
          <p className="font-body text-white/70 text-base sm:text-lg max-w-xl mx-auto">
            Каталог бесплатный — насовсем. Подписка снимает дневные лимиты на распознавание текста и подборки.
          </p>
        </motion.div>

        {/* Карточки тарифов */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 items-stretch"
        >
          {/* Бесплатный план */}
          <div className="flex flex-col rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-6 sm:p-8">
            <h3 className="font-display font-semibold text-white text-lg mb-1">Бесплатно</h3>
            <div className="flex items-baseline gap-2 mb-6">
              <span className="font-display font-bold text-white text-4xl tabular-nums">0 ₽</span>
              <span className="font-body text-white/50 text-sm">навсегда</span>
            </div>

            <ul className="flex flex-col gap-3 mb-8">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 font-body text-white/75 text-sm leading-relaxed">
                  <CheckIcon />
                  {f}
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={scrollToFinal}
              className="mt-auto inline-flex items-center justify-center min-h-[44px] px-5 py-2.5 rounded-xl border border-white/20 text-white hover:bg-white/10 text-sm font-semibold font-body transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              Начать бесплатно
            </button>
          </div>

          {/* Подписка */}
          <div
            className="relative flex flex-col rounded-3xl border border-[#b7102a]/60 bg-white/[0.06] backdrop-blur-sm p-6 sm:p-8"
            style={{ boxShadow: '0 24px 48px -24px rgba(183,16,42,0.35)' }}
          >
            <span className="absolute -top-3 left-6 sm:left-8 px-3 py-1 rounded-full bg-[#b7102a] text-white text-xs font-semibold font-body tracking-wide">
              Снимает лимиты
            </span>

            <h3 className="font-display font-semibold text-white text-lg mb-1">Подписка</h3>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="font-display font-bold text-white text-4xl tabular-nums">{monthly.priceRub} ₽</span>
              <span className="font-body text-white/50 text-sm">в месяц</span>
            </div>
            <p className="font-body text-[#f5a85a] text-sm mb-6">
              или {yearly.priceRub} ₽ за год — выходит {yearlyPerMonthRub()} ₽/мес, {yearlyFreeMonths()} месяца в подарок
            </p>

            <ul className="flex flex-col gap-3 mb-8">
              {PAID_FEATURES.map((f) => (
                <li key={f} className="flex items-start gap-2.5 font-body text-white/75 text-sm leading-relaxed">
                  <CheckIcon accent />
                  {f}
                </li>
              ))}
            </ul>

            <Link
              href="/unlimited"
              className="mt-auto inline-flex items-center justify-center min-h-[44px] px-5 py-2.5 rounded-xl bg-[#b7102a] hover:bg-[#991020] active:scale-[0.98] text-white text-sm font-semibold font-body transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b7102a]/40"
              style={{ boxShadow: '0 8px 20px -6px rgba(183,16,42,0.45)' }}
            >
              Получить безлимит
            </Link>
          </div>
        </motion.div>

        {/* Мелкий шрифт: оплата и оферта */}
        <motion.p
          initial={reduced ? false : { opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
          className="font-body text-white/50 text-sm text-center mt-8 max-w-xl mx-auto leading-relaxed"
        >
          Оплата картой через Робокассу. Подписку можно отменить в любой момент — доступ
          сохранится до конца оплаченного периода. Подробности — в{' '}
          <Link href="/offer" className="underline hover:text-white/80 transition-colors">оферте</Link>.
        </motion.p>
      </div>
    </section>
  );
}
